import React from 'react';
import { Link } from 'react-router-dom';
import SharedLayout from '@/components/SharedLayout';
import SubpageNavigation from '@/components/SubpageNavigation';
import PomodoroGallery from '@/components/PomodoroGallery';
import { FAQ } from '@/components/FAQ';
import SEOHead from '@/components/SEOHead';
import { CANONICAL_URLS } from '@/utils/canonicalUrls';
import { faqData, generateFAQSchema } from '@/data/faqData';

const Overview = () => {

  const webAppSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Free Pomodoro Timer Online",
    "alternateName": "Aesthetic Pomodoro Timer",
    "description": "Free online Pomodoro timer with aesthetic backgrounds, customizable focus sessions, task management, Kanban boards, and Eisenhower Matrix. No signup required.",
    "url": "https://freepomodorotimer.com",
    "applicationCategory": "ProductivityApplication",
    "operatingSystem": "Web Browser",
    "browserRequirements": "Requires JavaScript enabled",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "1250",
      "bestRating": "5"
    },
    "featureList": [
      "25-minute Pomodoro timer sessions",
      "Customizable work and break intervals",
      "Audio notifications and alerts",
      "Task management and tracking",
      "Kanban board workflow",
      "Eisenhower priority matrix",
      "Aesthetic background images from Unsplash",
      "Dark and light mode themes",
      "No registration required",
      "Works offline"
    ]
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Use a Pomodoro Timer for Maximum Focus",
    "description": "Step-by-step guide to using the Pomodoro Technique with our free online timer for better focus and productivity.",
    "totalTime": "PT2H",
    "step": [
      { "@type": "HowToStep", "position": 1, "name": "Start the Timer", "text": "Click Start to begin your 25-minute focused work session." },
      { "@type": "HowToStep", "position": 2, "name": "Focus on One Task", "text": "Work on a single task with complete concentration until the timer rings." },
      { "@type": "HowToStep", "position": 3, "name": "Take a Short Break", "text": "Take a 5-minute break to walk around, breathe, and relax." },
      { "@type": "HowToStep", "position": 4, "name": "Repeat the Cycle", "text": "After 4 Pomodoros, take a longer break of 15–30 minutes." },
      { "@type": "HowToStep", "position": 5, "name": "Track Your Progress", "text": "Continue as needed to stay productive without burning out." }
    ]
  };

  const faqSchema = generateFAQSchema(faqData);
  
  const combinedSchema = [webAppSchema, howToSchema, faqSchema];

  return (
    <>
      <SEOHead
        title="Aesthetic Pomodoro Timer — Free Online Focus Timer 2026"
        description="The most aesthetic Pomodoro timer online. Free 25-minute focus sessions with beautiful Unsplash backgrounds, lofi-friendly design & ADHD-friendly intervals. No signup."
        keywords="aesthetic pomodoro timer, cute pomodoro timer, lofi pomodoro timer, study timer aesthetic, adhd pomodoro timer, pomodoro timer with music, free pomodoro timer, pomodoro technique, focus timer, study timer"
        canonicalUrl={CANONICAL_URLS.home}
        schemaData={combinedSchema}
      />
      <SharedLayout>
        <article className="text-left px-6 py-16 max-w-3xl mx-auto space-y-16 text-foreground">
          <SubpageNavigation />

          <header className="space-y-6 border-b border-border/60 pb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Free · No signup · Browser-based</p>
            <h1 className="text-3xl md:text-4xl font-bold leading-tight">
              An aesthetic Pomodoro timer for focused, calmer work.
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              A distraction-free focus timer with cinematic Unsplash backgrounds and ADHD-friendly 25-minute intervals — built for studying, deep work, and the proven Pomodoro Technique.
            </p>
          </header>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">What is the Pomodoro Technique?</h2>
            <p className="text-muted-foreground leading-relaxed">
              Developed by Francesco Cirillo in the late 1980s, the Pomodoro Technique breaks work into short focused intervals — typically <strong className="text-foreground font-medium">25 minutes</strong> — followed by a 5-minute break. After four Pomodoros, you take a longer 15–30 minute break. The rhythm helps maintain concentration, reduce fatigue, and make steady progress.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Whether you're studying for exams, coding, writing, or working remotely, it builds momentum and beats procrastination — no signup or download required.
            </p>
          </section>

          <section className="space-y-6">
            <div className="space-y-3">
              <h2 className="text-2xl font-semibold">A complete productivity toolkit</h2>
              <p className="text-muted-foreground leading-relaxed">
                Three proven methods, designed to work together. Prioritize with the Matrix, organize with Kanban, execute with Pomodoro.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border rounded-lg overflow-hidden border border-border">
              {[
                { to: '/pomodoro', label: 'Pomodoro Timer', desc: '25-minute focus sessions with customizable breaks. Built for ADHD, studying, and deep work.', cta: 'Learn the technique' },
                { to: '/eisenhower', label: 'Eisenhower Matrix', desc: 'Prioritize by urgency and importance. The 4-quadrant system used by presidents and CEOs.', cta: 'Prioritize tasks' },
                { to: '/kanban', label: 'Kanban Board', desc: 'Visualize workflow and limit work-in-progress. The Toyota-inspired method for continuous improvement.', cta: 'Explore the method' },
              ].map((tool) => (
                <Link
                  key={tool.to}
                  to={tool.to}
                  className="group bg-card p-6 flex flex-col gap-3 hover:bg-accent transition-colors"
                >
                  <h3 className="text-base font-semibold">{tool.label}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1">{tool.desc}</p>
                  <span className="text-sm font-medium text-foreground group-hover:underline underline-offset-4">
                    {tool.cta} →
                  </span>
                </Link>
              ))}
            </div>

            <p className="text-sm text-muted-foreground italic pt-2">
              Matrix → Kanban → Pomodoro. Decide what matters, organize the flow, then focus.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">How to use the timer</h2>
            <ol className="space-y-3 text-muted-foreground leading-relaxed">
              {[
                ['Start', 'Begin a 25-minute focus session.'],
                ['Focus', 'Work on one task until the timer rings.'],
                ['Break', 'Step away for 5 minutes — walk, breathe, rest.'],
                ['Repeat', 'After four Pomodoros, take a longer 15–30 minute break.'],
              ].map(([step, text], i) => (
                <li key={step} className="flex gap-4">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground/60 w-8 pt-1">{String(i + 1).padStart(2, '0')}</span>
                  <span><strong className="text-foreground font-medium">{step}.</strong> {text}</span>
                </li>
              ))}
            </ol>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">Who it's for</h2>
            <ul className="space-y-2 text-muted-foreground leading-relaxed">
              <li><strong className="text-foreground font-medium">Students</strong> — structure study sessions and avoid cramming fatigue.</li>
              <li><strong className="text-foreground font-medium">Remote workers</strong> — maintain focus without office structure.</li>
              <li><strong className="text-foreground font-medium">People with ADHD</strong> — short intervals make tasks feel manageable.</li>
              <li><strong className="text-foreground font-medium">Writers & creatives</strong> — overcome blocks with structured sprints.</li>
              <li><strong className="text-foreground font-medium">Developers</strong> — ideal for debugging and coding sprints.</li>
            </ul>
          </section>

          <section className="space-y-6">
            <div className="space-y-3">
              <h2 className="text-2xl font-semibold">Aesthetic backgrounds for calm & clarity</h2>
              <p className="text-muted-foreground leading-relaxed">
                Curated Unsplash photography — sunlit beaches, peaceful forests, golden sunsets — turns your workspace into a window onto somewhere inspiring. A clearer environment leads to a clearer mind.
              </p>
            </div>
            <PomodoroGallery />
          </section>

          <FAQ />
        </article>
      </SharedLayout>

      <style>
        {`
          .glass-panel {
            border-radius: 16px !important;
            backdrop-filter: blur(10px);
            background: rgba(0, 0, 0, 0.3);
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
            transition: all 0.3s ease;
          }
          .nav-bar img {
            text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
            filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.7));
          }
          .seo-content {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border-width: 0;
          }
        `}
      </style>
    </>
  );
};

export default Overview;